import { useCallback, useEffect, useState } from 'react';

import DiseaseActivityHistoryApi from 'api/DiseaseActivityHistoryApi';
import { ApiDateType } from 'client';
import * as dto from 'client/dto';
import { DiseaseActivityHistoryLoadCount } from 'utils/constants';
import { getDateStrToday } from 'utils/dateTimeHelpers';
import { format } from 'date-fns';

export function usePaginatedActivityHistory(
  disactIds: dto.CountryDiseaseActivityKey,
  activityDate: ApiDateType,
  excludeCaseCounts: boolean
) {
  const [data, setData] = useState<dto.DailyActivityListModel>({
    dailyReports: [],
    hasNewerEntries: false,
    hasOlderEntries: false,
  });
  const [isLoadingInitial, setisLoadingInitial] = useState(false);
  const [isLoadingBelow, setisLoadingBelow] = useState(false);
  const [isLoadingAbove, setisLoadingAbove] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(null);
  const [scrollToIndex, setScrollToIndex] = useState<number>(null);

  //-------------------------------------------------------------------------------------------------------------------------------------

  const loadBelow = useCallback(() => {
    setisLoadingBelow(true);
    setScrollToIndex(null);
    const payload = {
      diseaseId: disactIds?.diseaseId,
      speciesId: disactIds?.speciesId,
      countryId: disactIds?.countryId,
      activityDate:
        data.dailyReports.length > 0
          ? data.dailyReports[data.dailyReports.length - 1]?.reportDate || getDateStrToday()
          : getDateStrToday(),
      count: DiseaseActivityHistoryLoadCount.initOrMoreLoads,
      excludeCaseCounts: excludeCaseCounts,
    };
    const apiPromise = disactIds?.countryId
      ? DiseaseActivityHistoryApi.GetOlderCountryDiseaseActivityUpdates(payload)
      : DiseaseActivityHistoryApi.GetOlderGlobalDiseaseActivityUpdates(payload);
    apiPromise
      .then(dataNew => {
        setData({
          dailyReports: [
            ...data?.dailyReports,
            ...(dataNew?.dailyReports || []), // NOTE: below
          ],
          hasNewerEntries: data?.hasNewerEntries || false,
          hasOlderEntries: dataNew?.hasOlderEntries || false,
        });
      })
      .finally(() => {
        setisLoadingBelow(false);
      });
  }, [disactIds, excludeCaseCounts, data]);

  //-------------------------------------------------------------------------------------------------------------------------------------

  const loadAbove = useCallback(() => {
    setisLoadingAbove(true);
    setScrollToIndex(null);
    const payload = {
      diseaseId: disactIds?.diseaseId,
      speciesId: disactIds?.speciesId,
      countryId: disactIds?.countryId,
      activityDate: data.dailyReports[0]?.reportDate || getDateStrToday(),
      count: DiseaseActivityHistoryLoadCount.initOrMoreLoads,
      excludeCaseCounts: excludeCaseCounts,
    };
    const apiPromise = disactIds?.countryId
      ? DiseaseActivityHistoryApi.GetNewerCountryDiseaseActivityUpdates(payload)
      : DiseaseActivityHistoryApi.GetNewerGlobalDiseaseActivityUpdates(payload);
    apiPromise
      .then(dataNew => {
        setData({
          dailyReports: [
            ...dataNew.dailyReports, // NOTE: above
            ...data?.dailyReports,
          ],
          hasNewerEntries: dataNew.hasNewerEntries,
          hasOlderEntries: data?.hasOlderEntries || false,
        });
        setScrollToIndex(dataNew.dailyReports.length > 0 ? dataNew.dailyReports.length - 1 : null);
        if (activeIndex != null) setActiveIndex(activeIndex + dataNew.dailyReports.length);
      })
      .finally(() => {
        setisLoadingAbove(false);
      });
  }, [disactIds, activeIndex, excludeCaseCounts, data]);

  //-------------------------------------------------------------------------------------------------------------------------------------

  useEffect(() => {
    setisLoadingInitial(true);
    setActiveIndex(null);
    setScrollToIndex(null);
    const payload = {
      diseaseId: disactIds?.diseaseId,
      speciesId: disactIds?.speciesId,
      countryId: disactIds?.countryId,
      activityDate: activityDate || getDateStrToday(),
      offset: activityDate
        ? DiseaseActivityHistoryLoadCount.deepLinkDatePad
        : DiseaseActivityHistoryLoadCount.initOrMoreLoads,
      excludeCaseCounts: excludeCaseCounts,
    };
    const apiPromise = disactIds?.countryId
      ? DiseaseActivityHistoryApi.GetCountryDiseaseActivityUpdates(payload)
      : DiseaseActivityHistoryApi.GetGlobalDiseaseActivityUpdates(payload);
    apiPromise
      .then(dataNew => {
        setData({
          dailyReports: dataNew.dailyReports || [],
          hasNewerEntries: dataNew.hasNewerEntries || false,
          hasOlderEntries: dataNew.hasOlderEntries || false,
        });
        if (activityDate) {
          const achorIndex = dataNew.dailyReports.findIndex(
            x => format(new Date(x.reportDate), 'yyyy-MM-dd') === activityDate
          );
          setActiveIndex(achorIndex);
          setScrollToIndex(achorIndex);
        } else {
          setActiveIndex(null);
          setScrollToIndex(null);
        }
      })
      .finally(() => {
        setisLoadingInitial(false);
      });
  }, [disactIds, activityDate, excludeCaseCounts]);

  return {
    data,
    activeIndex,
    scrollToIndex,
    isLoadingInitial,
    isLoadingBelow,
    isLoadingAbove,
    loadBelow,
    loadAbove,
  };
}
